import { DESIGN_SLIDES } from './designSlides';
import { DEV_SLIDES } from './devSlides';
import {
  linearIssues,
  outline,
  pullRequests,
  question,
  say,
  step,
  thought,
  webSearch,
  type DemoLiveTurn,
  type DemoTurn,
} from './demoThread';
import { IDEA_SLIDES } from './ideaSlides';
import { PITCH_SLIDES } from './pitchSlides';
import type { DemoSlide } from './slideSvg';
import { UPDATE_SLIDES } from './updateSlides';

/**
 * One hero use-case: the conversation so far, the turn that writes the deck, and the deck.
 *
 * `history` is what the user scrolls up through: several real exchanges, each with the tools the agent
 * actually has (connector reads, web search, a question, the outline) and the iterations that shaped the
 * story. The demo opens on `live`, already writing. Tool rows use the backend's own finished wording
 * (`ToolStepLabels`), so the record reads the way a reloaded thread does.
 */
export interface HeroDemoScript {
  id: string;
  /** The tab's label above the window. */
  label: string;
  deckTitle: string;
  history: DemoTurn[];
  live: DemoLiveTurn;
  slides: DemoSlide[];
}

const YESTERDAY = 24 * 60;

export const HERO_SCRIPTS: HeroDemoScript[] = [
  {
    id: 'pitch',
    label: 'Pitch a startup',
    deckTitle: 'Offseason, seed round',
    history: [
      {
        user: 'Seed deck for Offseason. Twenty minutes with angels who know travel. The numbers are in the sheet and the photos are from our first homes.',
        files: ['offseason-metrics.csv', 'first-homes.zip'],
        minutesAgo: YESTERDAY + 45,
        agent: [
          thought(
            'Investors in travel will ask two things: why these homes are empty, and whether guests come back. The sheet has nine months of bookings. The photos are strong enough to carry the product page.',
            8,
          ),
          step('Read your files'),
          step('Reviewed your images'),
          say('I read both. Your strongest line is the empty nights: owners use their homes about 65 nights a year. Bookings grew every month, to 810 nights in September.'),
          question('Should it open on the empty homes, or on the guests who want a quiet week?', ['The empty homes', 'The guests'], 'The empty homes'),
        ],
      },
      {
        user: 'Make it feel like a real product, not a spreadsheet. Show the app.',
        minutesAgo: YESTERDAY + 30,
        agent: [
          thought('The product page should be the app itself, with a real listing from their photos. Each number gets one page.', 5),
          step('Saved the point card'),
          outline([
            ['The idea', ['Cover', 'The problem', 'The product']],
            ['The proof', ['How it is going', 'The market']],
            ['The business', ['How we make money', 'Why owners stay', 'The ask']],
          ]),
          say('The point is now: the homes already exist, they only need a way to be booked. The app gets its own page with a listing from your photos, and each number gets one page.'),
        ],
      },
      {
        user: 'How many second homes are there near cities? I want a market number I can defend.',
        minutesAgo: 20,
        agent: [
          thought('Look for counts of second homes in Europe, and how many sit within a few hours of a large city.', 6),
          webSearch('second homes in Europe near large cities', [
            'eurostat.ec.europa.eu · Housing in Europe',
            'statista.com · Second homes in Europe',
            'oecd.org · Affordable housing database',
          ]),
          say('About 12 million second homes in Europe, and roughly half sit within three hours of a large city. I kept the conservative figure, 6 million, and put the whole chain on one page.'),
        ],
      },
    ],
    live: {
      user: 'Good. Write it, and keep the words out of the way of the photos.',
      thought: 'Eight artifacts. The cover and the product carry the photos. One number per page, short headlines.',
      thoughtSec: 3,
      opening: [{ say: 'They will judge it in the first minute, so the cover and the product carry the photos. Point card first.', step: 'Read the point card' }],
      lines: [
        'The cover is the headland photo and one line. It should feel like a weekend before it says anything.',
        'The problem as a calendar, so 300 empty nights are something you see, not read.',
        'The product as the app itself, with the clay house from your photos as the listing.',
        'Traction as one curve over nine months, with September called out at 810 nights.',
        'The market as three blocks, 12 million down to 6 million, with your 140 homes as a sliver.',
        'The model as a real receipt, so the 15% is a number they can check.',
        'Owners get their own page. Nine in ten listing again is the retention investors ask about.',
        'The ask comes last, €1.5M and what it buys, ending on three of the homes.',
      ],
      closing: [{ say: 'All eight are in. Reading it cold, like an angel who missed the meeting.', step: 'Read the deck as a stranger' }],
      outro: `## Offseason seed deck

Eight artifacts that make one case: the homes already exist, they only need a way to be booked.

- **Artifacts 1 to 3 (The idea):** The headland on the cover, 300 empty nights as a calendar, then the app with a real listing.
- **Artifacts 4 to 5 (The proof):** 2,900 nights in nine months, and 6 million homes within three hours of a city.
- **Artifacts 6 to 8 (The business):** 15% of every night on a real receipt, 92% of owners listing again, and the €1.5M ask.

A cold reader got the whole idea from the cover and the calendar alone.`,
    },
    slides: PITCH_SLIDES,
  },
  {
    id: 'design',
    label: 'Show design work',
    deckTitle: 'Basket, checkout',
    history: [
      {
        user: 'A case study of the checkout redesign for my portfolio. Hiring managers give it five minutes. The Figma file is Checkout v3.',
        files: ['interviews.md'],
        minutesAgo: YESTERDAY + 55,
        agent: [
          thought(
            'Checkout v3 has three directions and the shipped flow. interviews.md is twelve sessions. For a hiring manager the order is the problem, the process, the result.',
            7,
          ),
          step('Read a Figma file'),
          step('Read Figma frames'),
          step('Read your files'),
          say('I read the file and the twelve interviews. Three directions, one shipped, and the delivery fee comes up in nine of the twelve.'),
          question('Should it open on the drop-off you found, or on the new checkout?', ['The drop-off', 'The new checkout'], 'The drop-off'),
        ],
      },
      {
        user: 'Show the directions I killed too, not only the one that won.',
        minutesAgo: YESTERDAY + 41,
        agent: [
          thought('One artifact with all three side by side is enough. Both losers failed the same way in testing.', 4),
          outline([
            ['The problem', ['Cover', 'The problem', 'What we heard']],
            ['The work', ['Three tries', 'What shipped']],
            ['The result', ['What it did', 'What I learned']],
          ]),
          say('The drop-off opens it, then what people said, the three tries side by side, what shipped and what it did. Both losing directions failed the same question in testing: where is the fee?'),
        ],
      },
      {
        user: "How should it end? I don't want to end on a number.",
        minutesAgo: 15,
        agent: [
          thought('The number proves it. The lesson is the part a hiring manager remembers.', 6),
          step('Read the point card'),
          say('End on the lesson. The number proves it, the lesson is what they remember: show the whole price before you ask for anything else.'),
        ],
      },
    ],
    live: {
      user: "Yes. Write it in the Figma file's colours and use the real frames.",
      thought: "Seven artifacts. The file's green, lime and orange. The before and after screens come from the frames.",
      thoughtSec: 4,
      opening: [{ say: 'The phones should be your real screens, so I am reading the frames again first.', step: 'Read Figma frames' }],
      lines: [
        'The cover shows the shipped checkout, because a designer is judged on the work before the words.',
        'The problem as three bars. One in three leaving at payment needs no paragraph.',
        'Three interview quotes on separate cards, so the fee complaint repeats by itself.',
        'All three tries side by side. Showing what you killed is what makes the choice believable.',
        'What shipped, as the two screens that matter: the fee up front and the paid screen.',
        'The result as before and after, 66% to 92%, with nothing else competing for the eye.',
        'The lesson closes it as one price summary with the fee in plain sight, the line they repeat in the debrief.',
      ],
      closing: [{ say: 'All seven are in. Reading it cold, like a hiring manager with five minutes.', step: 'Read the deck as a stranger' }],
      outro: `## Checkout case study, drop-off first

Seven artifacts that tell one story: people left because they could not see the price, and showing it early fixed it.

- **Artifacts 1 to 3 (The problem):** The shipped checkout on the cover, one in three leaving at payment, then three quotes about the fee.
- **Artifacts 4 to 5 (The work):** The three tries side by side, and the two screens that shipped, drawn from your frames.
- **Artifacts 6 to 7 (The result):** Paid went from 66% to 92%, and the lesson stands alone on the last page.

A cold reader named the fee as the whole story within the first three pages.`,
    },
    slides: DESIGN_SLIDES,
  },
  {
    id: 'update',
    label: 'Product update',
    deckTitle: 'Tempo, cycle 42',
    history: [
      {
        user: 'Company update from this cycle. Most people never open Linear, so tell them what changed for customers.',
        minutesAgo: 70,
        agent: [
          thought('Read the cycle, then sort by what a customer would notice. The status update should say why anything slipped.', 6),
          step('Read Linear cycles'),
          linearIssues([
            ['TEM-812', 'Shared calendars'],
            ['TEM-790', 'Search results under 200ms'],
            ['TEM-801', 'Dark mode on Android'],
            ['TEM-777', 'Invoices in any currency'],
          ]),
          step('Read Linear status updates'),
          say('Cycle 42 closed 23 issues. For customers one launch matters: shared calendars. Invoices slipped, the status update says a rounding bug.'),
          question('Do you want to lead with what shipped, or with what slipped?', ['What shipped', 'What slipped'], 'What shipped'),
        ],
      },
      {
        user: "Be honest about the slip, one page, no excuses. And add usage, it's in the csv.",
        files: ['usage-sept.csv'],
        minutesAgo: 58,
        agent: [
          thought('Weekly active teams by cycle. Check whether the jump lines up with the launch date.', 5),
          step('Read your files'),
          say('Weekly active teams went from 2,050 to 2,310. Most of the jump came in the three days after shared calendars went out. The slip gets its own page with the new date, October 5.'),
          outline([
            ['This cycle', ['Cover', 'The big one', 'Also shipped', 'What slipped']],
            ['What it moved', ['How usage moved', 'Next cycle']],
          ]),
        ],
      },
      {
        user: 'The eleven small fixes, do we list them?',
        minutesAgo: 12,
        agent: [
          thought('Eleven rows would bury the launch.', 3),
          say('I would put them in one line. Nobody outside the team needs them one by one, and eleven rows would bury the launch.'),
        ],
      },
    ],
    live: {
      user: 'Agreed. Write it, and keep it readable on a phone.',
      thought: 'Six artifacts. The launch first, the slip on its own page, the fixes as one line. Big type for a phone.',
      thoughtSec: 3,
      opening: [{ say: 'Most people will read this on a phone, so one idea per page and big type.', step: 'Read the point card' }],
      lines: [
        'The cover is only the cycle and its dates, so nobody wonders which update this is.',
        'Shared calendars get their own page with a real week of bookings. It is the one customers will notice.',
        'The smaller wins share a page, and the eleven fixes become one line.',
        'The slip shows the bug itself, one cent off on an invoice, so late reads as careful.',
        'Usage by cycle, so the launch has a number behind it.',
        'Next cycle closes it with three items, so the next update has something to be measured against.',
      ],
      closing: [{ say: 'All six are in. Reading it cold, like someone in sales who never opens Linear.', step: 'Read the deck as a stranger' }],
      outro: `## Cycle 42, for everyone outside the team

Six artifacts on what changed for customers, in the order they would care about it.

- **Artifacts 1 to 3 (This cycle):** Shared calendars lead, then search, dark mode and the eleven fixes as one line.
- **Artifact 4 (What slipped):** Invoices moved to October 5 because of a rounding bug, said plainly.
- **Artifacts 5 to 6 (What it moved):** Weekly teams up from 2,050 to 2,310, then the three things coming next.

A cold reader came away knowing shared calendars shipped and invoices are late, the two things that matter.`,
    },
    slides: UPDATE_SLIDES,
  },
  {
    id: 'idea',
    label: 'Explain an idea',
    deckTitle: 'No more Monday meeting',
    history: [
      {
        user: 'Turn my notes into something for the leads on Thursday. I want to replace the Monday status meeting with a written update.',
        files: ['rough-notes.md', 'whiteboard.jpg'],
        minutesAgo: YESTERDAY + 20,
        agent: [
          thought('The notes and the whiteboard sketch are the idea. The leads will want to know what it costs today. Last quarter there was a meeting survey, check Notion.', 7),
          step('Read your files'),
          step('Reviewed your images'),
          step('Searched Notion'),
          step('Read a Notion page'),
          say('Your notes and the whiteboard agree on the idea. I also found last quarter\'s survey in Notion: 31 of 40 people said the Monday meeting could be written.'),
          question('Should it open on what the meeting costs, or on what replaces it?', ['What it costs', 'What replaces it'], 'What it costs'),
        ],
      },
      {
        user: 'They will push back. What will they say?',
        minutesAgo: YESTERDAY + 9,
        agent: [
          thought('Three objections come up every time this is tried: face time, nobody reads it, problems hide.', 8),
          say('Three things, probably. We lose the face time. Nobody will read it. Problems will hide. Each has a short answer, and I would put all three on one page before they can raise them.'),
        ],
      },
      {
        user: 'Yes, and make the ask small.',
        minutesAgo: 10,
        agent: [
          thought('A trial with a way back is the easiest yes.', 4),
          outline([
            ['Why', ['Cover', 'What it costs']],
            ['The idea', ['The idea', 'How a week looks']],
            ['Getting to yes', ['What you will ask', 'The ask']],
          ]),
          say('The ask is a four week trial. If it does not work, the meeting comes back in week five. Six artifacts.'),
        ],
      },
    ],
    live: {
      user: 'Write it. Use the whiteboard sketch for the week.',
      thought: 'Six artifacts. The week view comes from the whiteboard photo. The cost is 40 people, one hour, every week.',
      thoughtSec: 4,
      opening: [{ say: 'Your whiteboard already has the week drawn, so I am starting from it.', step: 'Reviewed your images' }],
      lines: [
        'The cover is the meeting itself, struck out, so the idea lands before anyone reads a word.',
        'The cost comes first, as 2,080 squares, one for every hour the meeting takes in a year.',
        'The idea as the update itself, five lines from one team, so they see how small it is.',
        'The week from your sketch, before and after, so the freed hour is visible.',
        'The three objections get one page, answered before the leads can raise them.',
        'The ask stays small, a four week trial with a way back, so saying yes costs nothing.',
      ],
      closing: [{ say: 'All six are in. Reading it cold, like a lead who likes the Monday meeting.', step: 'Read the deck as a stranger' }],
      outro: `## No more Monday meeting

Six artifacts that argue for a written update, starting from what the meeting costs and ending on an easy yes.

- **Artifacts 1 to 2 (Why):** The meeting struck out on the cover, then 2,080 hours drawn as squares.
- **Artifacts 3 to 4 (The idea):** The written update itself, and the week from your whiteboard, before and after.
- **Artifacts 5 to 6 (Getting to yes):** Face time, reading and hidden problems answered, then a four week trial.

A cold reader who likes the meeting still reached the trial without a new question.`,
    },
    slides: IDEA_SLIDES,
  },
  {
    id: 'dev',
    label: 'Explain a dev process',
    deckTitle: 'Moving to Postgres',
    history: [
      {
        user: 'Walk the whole company through the Postgres move, not just engineers. The RFC is in the platform repo.',
        minutesAgo: YESTERDAY + 30,
        agent: [
          thought('Find RFC 118 in the platform repo, then the pull requests that already exist for each step.', 6),
          step('Searched GitHub'),
          step('Read a GitHub file'),
          pullRequests('tempo/platform', [
            ['2211', 'Dual write to Postgres'],
            ['2230', 'Backfill job, one table per night'],
            ['2244', 'Shadow reads with diff checks'],
          ]),
          say('RFC 118 has four steps over six weeks, and three of them already have pull requests. The reason is month end reports: 40 seconds today, under two on Postgres.'),
          question('Do you want the four steps on one page, or a page for each?', ['One page', 'A page each'], 'A page each'),
        ],
      },
      {
        user: 'Support and sales need to know what changes for them, and when.',
        files: ['runbook.md'],
        minutesAgo: YESTERDAY + 18,
        agent: [
          thought('The runbook says what customers see at each step. Support needs one line per step, even when it is nothing.', 5),
          step('Read your files'),
          say('Customers see nothing until step three, then faster reports. I will put a line for support under every step, even when the answer is nothing.'),
        ],
      },
      {
        user: 'What if it goes wrong?',
        minutesAgo: 9,
        agent: [
          thought('Until the last step the old database has everything, so rollback is a setting.', 4),
          outline([
            ['Why', ['Cover', 'Why we are moving', 'The plan']],
            ['The steps', ['Write to both', 'Copy the past', 'Read from the new one', 'Switch off the old one']],
            ['Safety', ['If it goes wrong']],
          ]),
          say('Until the last step the old database has everything, so going back is a setting, not a restore. Your runbook says you practised it twice in staging. That gets its own page.'),
        ],
      },
    ],
    live: {
      user: 'Write it. Plain words, nobody outside engineering should have to ask what a backfill is.',
      thought: 'Eight artifacts. Replace backfill, dual write and shadow reads with what they do in plain words.',
      thoughtSec: 5,
      opening: [{ say: 'Half the readers are not engineers, so every step has to say what it does. Point card first.', step: 'Read the point card' }],
      lines: [
        'The cover shows the move as two databases and an arrow, so anyone gets it before reading.',
        'Why comes first, as the 40 second report, because that is the pain sales already hears about.',
        'The plan fits on one page, four steps in six weeks, so every later page has a place to sit.',
        'Writing to both gets a customer line too. The answer is nothing, and that is the point.',
        'Copying the past warns about slower exports at night, the one thing support will be asked.',
        'Reading from the new one is where customers notice, so this page leads with faster reports.',
        'Switching off the old one says nothing is deleted for ninety days, which is what people worry about.',
        'Going back gets its own page, because the fear in the room will be what happens if it breaks.',
      ],
      closing: [{ say: 'All eight are in. Reading it cold, like someone in support.', step: 'Read the deck as a stranger' }],
      outro: `## Moving to Postgres, in plain words

Eight artifacts that walk the whole company through the move, with what customers see at every step.

- **Artifacts 1 to 3 (Why):** Two databases and an arrow, the 40 second report, then four steps in six weeks.
- **Artifacts 4 to 7 (The steps):** One page per step, each with what customers see and what support should say.
- **Artifact 8 (Safety):** Going back is one setting, practised twice in staging.

A cold reader in support knew what to say at every step, and the word backfill appears nowhere.`,
    },
    slides: DEV_SLIDES,
  },
];
